import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { Region } from '@heidi/schema/ml-scribe';
import { useMemo } from 'react';

const MARKET_RESTRICTED_REGIONS: ReadonlySet<Region> = new Set([
  Region.JP_AZ1,
  Region.UK,
  Region.EU,
  Region.SE_AZ1,
]);

/**
 * Compliance gate for plan/marketing features that must not appear in
 * restricted markets (UK, EU, Sweden enterprise, Japan).
 *
 * Fails closed while region or country is loading or errored so restricted
 * features never flash before resolution.
 */
export function useShouldHideMarketRestrictedFeatures() {
  const {
    data: userCountry,
    isPending: isUserPending,
    isSuccess: isUserSuccess,
  } = useQueryUser((user) => user?.country);
  const {
    data: kindeProperties,
    isPending: isKindePending,
    isError: isKindeError,
  } = useQueryKindeProperties();

  return useMemo(() => {
    const isPending = isUserPending || isKindePending;

    if (!isUserSuccess || isPending || isKindeError) {
      return { shouldHide: true, isPending };
    }

    const region = kindeProperties?.region;
    const shouldHide =
      userCountry === 'Japan' ||
      (region !== undefined && MARKET_RESTRICTED_REGIONS.has(region));

    return { shouldHide, isPending: false };
  }, [
    userCountry,
    kindeProperties?.region,
    isUserPending,
    isUserSuccess,
    isKindePending,
    isKindeError,
  ]);
}
